import * as React from 'react'
import * as classNames from 'classnames'
import * as style from './SuperHero.module.scss'
import * as csm from '../../../utils/csm'
import { HeroElement } from './HeroElement'
import DotNav from './DotNav'
import { StorefrontStateCollection, FeatureSwitches } from '../../../containers/Storefront';
import { StorefrontItem, WPSClient, withArgs, Args } from '@utils'
import { v2Request } from '../../../../../utils/EnrichItemMetadataUtils'

const NO_ACTIVE_INDEX = -1
const PREROLL_CONTINUE_SLIDER_DELAY = 2000
const AUTOSCROLL_DELAY = 5000

declare let window: Window & {
  P: any
}

enum Direction {
  BACKWARD = -1,
  FORWARD = 1
}

const realMod = (n: number, m: number) => {
  return ((n % m) + m) % m;
}

interface SuperHeroProps extends StorefrontStateCollection {
  isFrozen?: boolean
  formFactor: string
  featureSwitches?: FeatureSwitches
}

interface SuperHeroState {
  loopCompleted: boolean
  index: number
  hasInteracted: boolean
  pausedForPlayback: boolean
  enrichments: { [name: string]: StorefrontItem }
  hasError: boolean
  childHasLock: boolean
  carouselTransform?: React.CSSProperties
}

class SuperHeroComponent extends React.Component<SuperHeroProps & Args, SuperHeroState> {

  autoscrollInterval?: number
  rightButton?: HTMLButtonElement
  resizeListener?: EventListener
  touchXStart = 0
  enrichedOnce = false

  constructor(props: SuperHeroProps & Args) {
    super(props)
    this.state = {
      index: 0,
      hasInteracted: false,
      loopCompleted: false,
      pausedForPlayback: false,
      enrichments: {},
      hasError: false,
      childHasLock: false
    }
  }


  recalculateState(index: number) {
    return {
      index: typeof index === 'number' ? index : this.state.index,
      loopCompleted: this.state.loopCompleted
    }
  }

  addPlayerListeners() {
    if (!global.__NATIVEAPP__ && typeof window !== 'undefined' && window.P) {
      window.P.when('dv-player-integration').execute((player: any) => {
        player.runBeforePlay(() => {
          if (!this.shouldNotAutoscroll()) {
            this.setState({ pausedForPlayback: true })
          }
        })
        player.on('closed', () => {
          this.setState({ pausedForPlayback: false }, () => {
            if (!this.shouldNotAutoscroll()) {
              this.moveTo(this.state.index + 1)
              this.autoScrollElements()
            }
          })
        })
      })
    }
  }

  componentDidMount() {
    this.moveTo(0)
    this.setState(this.recalculateState(this.state.index))
    this.autoScrollElements()

    this.addPlayerListeners()
    this.enrichItems()
  }

  enrichItems() {
    if (!(this.props.featureSwitches && this.props.featureSwitches.ajaxSuperhero) || this.enrichedOnce)
      return
    const titleIDs = this.props.items.map(item => item.titleID).filter(Boolean) as string[]
    const wps = new WPSClient()
    const request = v2Request(titleIDs, "SUPERHERO", true, true, false, true)
    if (titleIDs && titleIDs.length > 0) {
      wps.EnrichItemMetadata({
        ...request,
        context: this.props.context
      }).then((r) => this.setState({ enrichments: r.enrichments }))
      this.enrichedOnce = true
    }
  }

  componentWillUnmount() {
    this.removeEventListeners()
    if (this.autoscrollInterval) {
      this.stopAutoscroll()
    }
  }

  componentDidCatch(error: Error) {
    csm.count('DVWeb::Storefront::JSFatal::SuperHero')
    csm.logError(error, '[DVWeb::Storefront] Error while trying to render a SuperHero')
    this.setState({ hasError: true })
  }

  handleMouseLeave = () => {
    this.stopAutoscroll()
    if (!this.state.childHasLock) {
      this.autoScrollElements()
    }
  }

  handleMouseEnter = () => {
    this.stopAutoscroll()
  }

  changeLock = (locked: boolean) => {
    this.setState({ childHasLock: locked })
    if (locked || this.shouldNotAutoscroll()) {
      this.stopAutoscroll()
      return
    }
    const idx = this.state.index
    setTimeout(() => {
      this.moveTo(idx + 1)
      this.autoScrollElements()
    }, PREROLL_CONTINUE_SLIDER_DELAY)

  }

  removeEventListeners() {
    this.resizeListener && window.removeEventListener('resize', this.resizeListener)
  }

  handleTouchStart = (evt: React.TouchEvent) => {
    this.touchXStart = evt.changedTouches[0].clientX
  }

  handleTouchEnd = (evt: React.TouchEvent) => {
    const DELTAX = 20
    const newTouchX = evt.changedTouches[0].clientX
    const isRTL = this.props.render && this.props.render.isRTL
    const directionRight = isRTL ? Direction.BACKWARD : Direction.FORWARD
    const directionLeft = isRTL ? Direction.FORWARD : Direction.BACKWARD
    if (Math.abs(newTouchX - this.touchXStart) > DELTAX) {
      if (evt.changedTouches[0].clientX > this.touchXStart)
        this.triggerInteractionScroll(directionLeft)
      else
        this.triggerInteractionScroll(directionRight)
    }
  }

  handleKeyDown = (evt: React.KeyboardEvent) => {
    const TAB_KEYCODE = 9
    const ENTER_KEYCODE = 13
    const prevIndexOffset = -1
    const nextIndexOffset = 1
    const currentIndex = this.state.index

    /*
   * @desc if the element is on focus we enable shortcuts in 2 ways:
   * 1. TAB key - if the tab is pressed go to the next slide unless you are
   * focusing on the last page. If you press the SHIFT key along with tab
   * go to the previous slide unless you are focusing on the firt page.
   * 2. ENTER key - if you press the enter key while focusing on the tab you will
   * be redirected to the link in the page.
   */
    if (evt.which === TAB_KEYCODE) {
      const nextIndex = currentIndex + (evt.shiftKey ? prevIndexOffset : nextIndexOffset)
      if ((evt.shiftKey && nextIndex >= 0) ||
        (!evt.shiftKey && nextIndex < this.props.items.length)) {
        evt.preventDefault()
        this.stopAutoscroll()
        this.moveTo(nextIndex)
      }
    } else if (evt.which === ENTER_KEYCODE) {
      const currentItem = this.props.items[currentIndex]
      const targetUrl = currentItem && currentItem.link && currentItem.link.url
      if (targetUrl)
        window.location.href = targetUrl
    }
  }

  /* TODO: AVWebStorefront-718 - Integration Tests */
  /* istanbul ignore next */
  updateResize() {
    this.setState(this.recalculateState(this.state.index), () => {
      this.moveTo(this.state.index)
    })
  }

  shouldComponentUpdate(nextProps: SuperHeroProps) {
    if (nextProps.items.length !== this.props.items.length) {
      this.moveTo(0)
    }

    return true
  }

  componentDidUpdate(prevProps: SuperHeroProps) {
    this.enrichItems()
    if (prevProps.isFrozen && !this.props.isFrozen && !this.state.childHasLock) {
      this.autoScrollElements()
    }
  }

  stopAutoscroll() {
    clearInterval(this.autoscrollInterval)
    this.autoscrollInterval = 0
  }

  shouldNotAutoscroll() {
    return (this.props.isFrozen || this.state.loopCompleted ||
      (this.state.hasInteracted) || this.state.pausedForPlayback || this.state.childHasLock
    )
  }

  autoScrollElements() {
    if (this.shouldNotAutoscroll() ||
      this.props.items[this.state.index].prerolls || this.autoscrollInterval)
      return

    this.autoscrollInterval = window.setInterval(() => {
      let activeIndex = this.state.index

      if (this.state.index === this.props.items.length - 1) {
        activeIndex = NO_ACTIVE_INDEX
        this.setState({
          hasInteracted: true
        })
        this.stopAutoscroll()
      }
      this.moveTo(activeIndex + 1)
    }, AUTOSCROLL_DELAY)
  }

  moveTo(scrollIndex: number) {
    // If we are scrolling, ensure the lock state is clean
    this.setState({ childHasLock: false })
    if (this.props.items.length < 1)
      return
    const sanitisedIndex = realMod(scrollIndex, this.props.items.length)
    const isScrollingPastEnd = scrollIndex >= this.props.items.length

    if (isScrollingPastEnd)
      this.setState({ loopCompleted: true })


    const direction = this.props.render && this.props.render.isRTL ? Direction.FORWARD : Direction.BACKWARD
    this.setState({
      ...this.recalculateState(sanitisedIndex),
      carouselTransform: { transform: `translateX(${sanitisedIndex * 100 * direction}%)` }
    }
    )
  }

  triggerInteractionScroll(direction = Direction.FORWARD) {
    this.stopAutoscroll()
    this.moveTo(this.state.index + direction)
    csm.count('SuperheroScroll')
  }


  itemsWithEnrichments() {
    if (!this.state.enrichments)
      return this.props.items
    const patchedItems = this.props.items.map(item => {
      const titleID = item.titleID
      const enrichment = titleID && this.state.enrichments[titleID]
      if (!enrichment)
        return item
      return {
        ...item,
        prerolls: enrichment.prerolls,
        watchlistAction: enrichment.watchlistAction,
        playbackAction: enrichment.playbackAction
      }
    })
    return patchedItems
  }

  isMinimumIndex() {
    return !this.state.index
  }

  isMaxmimumIndexHide() {
    return this.state.index === this.props.items.length - 1
  }

  isMaxmimumIndexDisable() {
    return this.state.index === this.props.items.length
  }

  hidePrev() {
    return this.props.render && this.props.render.isRTL ? this.isMaxmimumIndexHide() : this.isMinimumIndex()
  }

  disablePrev() {
    return this.props.render && this.props.render.isRTL ? this.isMaxmimumIndexDisable() : this.isMinimumIndex()
  }

  hideNext() {
    return this.props.render && this.props.render.isRTL ? this.isMinimumIndex() : this.isMaxmimumIndexHide()
  }

  disableNext() {
    return this.props.render && this.props.render.isRTL ? this.isMinimumIndex() : this.isMaxmimumIndexDisable()
  }

  render() {
    if (this.state.hasError)
      return <></>
    return (
      <div tabIndex={0}
        className={classNames(style.superhero, 'tst-superhero')}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        onKeyDown={this.handleKeyDown}>
        <div className={style.container}>
          {<button
            className={classNames({ [style.hidden]: this.hidePrev() },
              style.handle,
              style.leftHandle)}
            value="prev"
            onClick={() =>
              this.triggerInteractionScroll(this.props.render && this.props.render.isRTL ? Direction.FORWARD : Direction.BACKWARD)}
            disabled={this.disablePrev()}>
            <div className={style.icon}></div>
          </button>
          }
          <ul
            style={this.state.carouselTransform}
            onTouchStart={this.handleTouchStart}
            onTouchEnd={this.handleTouchEnd}
            className={classNames(
              'tst-superhero-item',
              style.animate,
              style.slider,
            )}>
            {this.itemsWithEnrichments().map((item, idx) => {
              const isActive = this.state.index === idx
              const canPlay = isActive && !this.props.isFrozen && !this.state.loopCompleted
              return <li key={item.image && item.image.url}>
                <HeroElement
                  loadNow={idx < 2}
                  item={item}
                  formFactor={this.props.formFactor}
                  featureSwitches={this.props.featureSwitches}
                  index={idx}
                  isActive={this.state.index === idx}
                  canPlay={canPlay}
                  changeLock={this.changeLock}
                />
              </li>
            })}
          </ul>
          {<button
            value="next"
            className={classNames(
              { [style.hidden]: this.hideNext() },
              style.handle,
              style.rightHandle)}
            onClick={() =>
              this.triggerInteractionScroll(this.props.render && this.props.render.isRTL ? Direction.BACKWARD : Direction.FORWARD)}
            disabled={this.disableNext()}>
            <div className={style.icon}></div>
          </button>
          }
          {this.props.items.length > 1 &&
            <DotNav size={this.props.items.length}
              activeIndex={this.state.index}
              moveTo={(scrollIndex: number) => {
                this.moveTo(scrollIndex)
                csm.count('SuperheroScroll')
              }}
            />
          }
        </div>
      </div>
    )
  }
}

export default withArgs(SuperHeroComponent) as React.ComponentType<SuperHeroProps>
